import { createContext, useContext, useMemo, type ReactNode } from "react"

export type DateRangeCalendarDensityUnit =
  | "labels"
  | "reviews"
  | "conversations"
export type DateRangeCalendarStatus = "idle" | "loading" | "error" | "ready"

export interface DateRangeCalendarVisibleGrid {
  startDate: string
  endDate: string
  timeZone: string
}

export interface DateRangeCalendarContextValue {
  density: ReadonlyMap<string, number>
  densityUnit?: DateRangeCalendarDensityUnit
  status: DateRangeCalendarStatus
  densityHelperText?: string
  selectionColor?: "blue"
  onVisibleGridChange?: (grid: DateRangeCalendarVisibleGrid) => void
  timeZone?: string
  onMonthChange?: (month: Date) => void
}

const Context = createContext<
  (DateRangeCalendarContextValue & { maximumDensity: number }) | undefined
>(undefined)

export function useDateRangeCalendarContext() {
  return useContext(Context)
}

interface ProviderProps extends DateRangeCalendarContextValue {
  children: ReactNode
  timeZone?: string
}

export function DateRangeCalendarContextProvider({
  children,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  density,
  densityUnit,
  status,
  densityHelperText,
  selectionColor,
  onVisibleGridChange,
  onMonthChange,
}: ProviderProps) {
  const value = useMemo(
    () => ({
      density,
      maximumDensity: Math.max(0, ...density.values()),
      densityUnit,
      status,
      densityHelperText,
      selectionColor,
      onVisibleGridChange,
      timeZone,
      onMonthChange,
    }),
    [
      density,
      densityHelperText,
      densityUnit,
      onMonthChange,
      onVisibleGridChange,
      selectionColor,
      status,
      timeZone,
    ],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}
